import type { FrontendVerticalDefinition } from '@affine/core/modules/streamlain/constants/vertical-definitions';
import { VERTICAL_DEFINITIONS } from '@affine/core/modules/streamlain/constants/vertical-definitions';
import type { I18nString } from '@affine/i18n';
import type { ReactNode } from 'react';

import ElsterLogo from './erica/elster-logo.png';
import { EricaSettingPanel } from './erica/setting-panel';
// sl-xk37: MCP moved to dedicated "MCP Access" tab in Roles & Access section

type IntegrationCard = {
  id: string;
  name: I18nString;
  desc: I18nString;
  icon: ReactNode;
  /** True if this card requires a cloud workspace */
  cloud?: boolean;
  /**
   * Backend feature key (matches plan_features.feature_key). When set, the
   * card is gated by the workspace plan and hidden when the feature has
   * `visibility: 'hidden'`. When omitted, the card is always visible.
   */
  featureKey?: string;
} & (
  | {
      setting: ReactNode;
    }
  | {
      link: string;
    }
);

const INTEGRATION_LIST = [
  // sl-za0v: Automation auto-provisions for Pro+ via `workflow-auto-provision`
  // (hidden capability) — no integration card needed. The legacy
  // streamlain-workflows BYOB/Hostinger card was removed.
  // sl-yphr: ELSTER integration
  {
    id: 'erica' as const,
    name: 'com.streamlain.integration.erica.name' as const,
    desc: 'com.streamlain.integration.erica.desc' as const,
    icon: (
      <img
        src={ElsterLogo}
        width={24}
        height={24}
        alt="ELSTER"
        style={{ borderRadius: 4 }}
      />
    ),
    setting: <EricaSettingPanel />,
    cloud: true,
    featureKey: 'elster' as const,
  },
] satisfies (IntegrationCard | false)[];

type IntegrationId = Exclude<
  Extract<(typeof INTEGRATION_LIST)[number], {}>,
  false
>['id'];

export type IntegrationItem = Exclude<IntegrationCard, 'id'> & {
  id: IntegrationId;
};

export function getAllowedIntegrationList(isCloudWorkspace: boolean) {
  return INTEGRATION_LIST.filter(item => {
    if (!item) return false;
    const requiredCloud = 'cloud' in item && item.cloud;
    if (requiredCloud && !isCloudWorkspace) return false;
    return true;
  }) as IntegrationItem[];
}

/**
 * sl-vw37.1.3: Get registered vertical definitions for the integration list.
 * Returns verticals sorted by their order field.
 * Only returns verticals for cloud workspaces (verticals require backend).
 */
export function getVerticalDefinitions(
  isCloudWorkspace: boolean
): FrontendVerticalDefinition[] {
  if (!isCloudWorkspace) return [];
  return VERTICAL_DEFINITIONS;
}
